import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Layout } from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { BookOpen, Users, TrendingUp, Clock, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Dashboard = () => {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const locale = language === "pt" ? "pt-BR" : language === "es" ? "es-ES" : "en-US";
    return date.toLocaleDateString(locale, { day: "2-digit", month: "short" });
  };

  return (
    <Layout>
      <div className="p-6 md:p-12" data-testid="dashboard-page">
        {/* Header */}
        <div className="mb-8">
          <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-2">
            {t("dashboard")}
          </p>
          <h1 className="font-serif text-3xl md:text-4xl">
            {t("hello")}, {user?.name?.split(" ")[0]}
          </h1>
        </div>

        {/* Welcome Message - Compacted */}
        <div className="bento-item mb-8 bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20 py-4 md:py-6">
          <h2 className="font-serif text-lg md:text-xl mb-2 text-primary">
            Seu espaço profissional
          </h2>
          <p className="text-sm text-muted-foreground">
            Tudo o que você precisa para estudar e organizar seus atendimentos.
          </p>
        </div>

        {/* Stats Grid */}
        <div className="bento-grid mb-12">
          <div className="bento-item" data-testid="stat-exams">
            <BookOpen size={24} strokeWidth={1} className="mb-4 text-muted-foreground" />
            <p className="stat-value">{loading ? "-" : stats?.exams_completed || 0}</p>
            <p className="stat-label">{t("exams_completed")}</p>
          </div>

          <div className="bento-item" data-testid="stat-score">
            <TrendingUp size={24} strokeWidth={1} className="mb-4 text-muted-foreground" />
            <p className="stat-value">{loading ? "-" : `${stats?.average_score || 0}%`}</p>
            <p className="stat-label">{t("average_score")}</p>
          </div>

          <div className="bento-item" data-testid="stat-clients">
            <Users size={24} strokeWidth={1} className="mb-4 text-muted-foreground" />
            <p className="stat-value">{loading ? "-" : stats?.clients_count || 0}</p>
            <p className="stat-label">{t("registered_clients")}</p>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          <Link to="/exams" className="block" data-testid="quick-exams">
            <div className="bento-item h-full flex items-center justify-between">
              <div>
                <h3 className="font-serif text-xl mb-2">{t("exams")}</h3>
                <p className="text-sm text-muted-foreground">
                  {t("practice_exams")}
                </p>
              </div>
              <ArrowRight size={24} strokeWidth={1} />
            </div>
          </Link>

          <Link to="/clients" className="block" data-testid="quick-clients">
            <div className="bento-item h-full flex items-center justify-between">
              <div>
                <h3 className="font-serif text-xl mb-2">{t("clients")}</h3>
                <p className="text-sm text-muted-foreground">
                  {t("manage_records")}
                </p>
              </div>
              <ArrowRight size={24} strokeWidth={1} />
            </div>
          </Link>
        </div>

        {/* Recent Activity */}
        <div className="bento-item" data-testid="recent-activity">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-serif text-xl">{t("recent_activity")}</h3>
            <Link to="/history" className="text-sm text-muted-foreground hover:text-foreground">
              {t("see_all")}
            </Link>
          </div>

          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-16 loading-skeleton" />
              ))}
            </div>
          ) : stats?.latest_attempts?.length > 0 ? (
            <div className="space-y-3">
              {stats.latest_attempts.map((attempt) => (
                <div
                  key={attempt.id}
                  className="flex items-center justify-between py-3 border-b last:border-b-0"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 border flex items-center justify-center">
                      <BookOpen size={18} strokeWidth={1} />
                    </div>
                    <div>
                      <p className="font-medium">{attempt.exam_title}</p>
                      <p className="text-xs text-muted-foreground font-mono">
                        {formatDate(attempt.completed_at)}
                      </p>
                    </div>
                  </div>
                  <div className={`text-lg font-serif ${
                    attempt.score >= 70 ? "text-green-600" : 
                    attempt.score >= 50 ? "text-yellow-600" : "text-red-600"
                  }`}>
                    {attempt.score}%
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state py-8">
              <Clock className="empty-state-icon" />
              <p className="empty-state-title">{t("no_activity")}</p>
              <p className="empty-state-description">
                {t("start_exam_or_client")}
              </p>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;
